// API Configuration
export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Account Statement Configuration
// States where account statement is required (must be included with notices)
export const STATEMENT_REQUIRED_STATES = ['TX'];

// States where account statement is optional but supported
export const STATEMENT_OPTIONAL_STATES = ['CA', 'FL', 'AZ', 'NV', 'CO', 'GA', 'NC', 'SC'];

// Helper to check if statement is required for a state
export const isStatementRequired = (state) => STATEMENT_REQUIRED_STATES.includes(state);

// Helper to check if statement is supported for a state
export const isStatementSupported = (state) => 
  STATEMENT_REQUIRED_STATES.includes(state) || STATEMENT_OPTIONAL_STATES.includes(state);

// States where amount is REQUIRED on preliminary notice (by statute)
export const AMOUNT_REQUIRED_STATES = [
  'CA', // California - required for mechanics lien claims
  // 'FL', // Florida - NOT required, only recommended
  'TX', // Texas - required for sworn statements
  'AZ', // Arizona - required per ARS § 33-992.01
  'NV', // Nevada - required
  'CO', // Colorado - required
  'GA', // Georgia - required for Notice to Contractor
  'LA', // Louisiana - required
  'WA', // Washington - required
  'OR', // Oregon - required
  'UT'  // Utah - required
];

// Helper to check if amount is required for a state
export const isAmountRequired = (state) => AMOUNT_REQUIRED_STATES.includes(state?.toUpperCase());

// Staff permission definitions
export const STAFF_PERMISSIONS = [
  // Core permissions
  { key: 'manage_research', label: 'Manage Research', description: 'Can view and submit research data' },
  { key: 'view_all_orders', label: 'View All Orders', description: 'Can view all orders (read-only for non-assigned)' },
  { key: 'assign_orders', label: 'Assign Orders', description: 'Can assign orders to staff members' },
  // Billing & Pricing permissions (hidden from staff by default)
  { key: 'view_billing', label: 'View Billing', description: 'Can view billing and pricing information', adminDefault: true },
  { key: 'set_pricing', label: 'Set Pricing', description: 'Can set pricing for orders' },
  { key: 'access_billing', label: 'Access Billing', description: 'Can view billing reports' },
  { key: 'manage_billing', label: 'Manage Billing', description: 'Can manage billing settings' },
  // User management
  { key: 'view_users', label: 'View Users', description: 'Can view user accounts' },
  { key: 'manage_users', label: 'Manage Users', description: 'Can create, edit, and deactivate users' },
  // Advanced permissions (admin-only by default)
  { key: 'send_mail', label: 'Send Mail', description: 'Can send mail directly (without admin review)', adminDefault: true },
  { key: 'process_advanced_docs', label: 'Process Advanced Docs', description: 'Can process liens, bonds, and advanced documents', adminDefault: true },
  { key: 'view_non_researched', label: 'View Non-Researched', description: 'Can view non-researched orders', adminDefault: true },
  { key: 'delete_recipients', label: 'Delete Recipients', description: 'Can delete recipients from notices', adminDefault: true }
];

// Recipient types for notices - Updated per user request
export const RECIPIENT_TYPES = [
  'Owner',
  'General Contractor',
  'Contractor',
  'Lender',
  'Bond Company',
  'Surety Agent',
  'Lien Agent',
  'Misc'
];

// Postage options
export const POSTAGE_TYPES = [
  { value: 'certified_ereturn', label: 'Certified Mail w/ E-Return Receipt' },
  { value: 'first_class', label: 'First Class' }
];

// Notice type definitions - includes all legacy and current formats
export const NOTICE_TYPES = [
  // Current format (underscore)
  { value: 'preliminary_notice_researched', label: 'Prelim (Researched)', requiresResearch: true },
  { value: 'preliminary_notice_not_researched', label: 'Prelim (Not Researched)', requiresResearch: false },
  { value: 'notice_of_intent_to_lien', label: 'Notice of Intent to Lien', requiresResearch: true },
  { value: 'mechanics_lien', label: 'Mechanics Lien', requiresResearch: true },
  { value: 'lien', label: 'Lien', requiresResearch: true },
  { value: 'claim_on_bond', label: 'Claim on Bond', requiresResearch: true },
  { value: 'satisfaction_of_lien', label: 'Satisfaction of Lien', requiresResearch: false },
  { value: 'research_only', label: 'Research Only', requiresResearch: true },
  { value: 'miller_act', label: 'Miller Act Notice', requiresResearch: true },
  { value: 'notice_of_non_payment', label: 'Notice of Non Payment', requiresResearch: true },
  { value: 'threat_to_lien', label: 'Threat to Lien', requiresResearch: true },
  { value: 'stop_payment_notice', label: 'Stop Payment Notice', requiresResearch: true },
  // New universal document types
  { value: 'notice_of_commencement', label: 'Notice of Commencement', requiresResearch: false },
  { value: 'notice_of_completion', label: 'Notice of Completion', requiresResearch: false },
  { value: 'notice_of_cessation', label: 'Notice of Cessation', requiresResearch: false },
  // Texas-specific notice types
  { value: 'tx_60_day', label: 'TX 60-Day Notice', requiresResearch: true },
  { value: 'tx_90_day', label: 'TX 90-Day Notice', requiresResearch: true },
  { value: 'tx_bonded', label: 'TX Bonded Notice', requiresResearch: true },
  { value: 'tx_retainage', label: 'TX Retainage Notice', requiresResearch: true },
  // Legacy formats (for backwards compatibility with old data)
  { value: 'Preliminary Notice (Researched)', label: 'Prelim (Researched) [Legacy]', requiresResearch: true, legacy: true },
  { value: 'Preliminary Notice (Not Researched)', label: 'Prelim (Not Researched) [Legacy]', requiresResearch: false, legacy: true },
  { value: 'prelim_researched', label: 'Prelim Researched', requiresResearch: true },
  { value: 'prelim_not_researched', label: 'Prelim Not Researched', requiresResearch: false }
];

// Helper to normalize notice type for display
export const normalizeNoticeType = (noticeType) => {
  if (!noticeType) return 'Unknown';
  
  // Map legacy formats to standardized labels
  const legacyMap = {
    'Preliminary Notice (Researched)': 'Prelim (Researched)',
    'Preliminary Notice (Not Researched)': 'Prelim (Not Researched)',
    'preliminary_notice_researched': 'Prelim (Researched)',
    'preliminary_notice_not_researched': 'Prelim (Not Researched)',
    'prelim_researched': 'Prelim (Researched)',
    'prelim_not_researched': 'Prelim (Not Researched)',
    'mechanics_lien': 'Mechanics Lien',
    'claim_on_bond': 'Claim on Bond',
    'satisfaction_of_lien': 'Satisfaction of Lien',
    'notice_of_intent_to_lien': 'Notice of Intent',
    'notice_of_non_payment': 'Notice of Non-Payment',
    'threat_to_lien': 'Threat to Lien',
    'notice_of_commencement': 'Notice of Commencement',
    'stop_payment_notice': 'Stop Payment Notice',
    'research_only': 'Research Only',
    'miller_act': 'Miller Act',
    'notice_of_completion': 'Notice of Completion',
    'notice_of_cessation': 'Notice of Cessation',
    'tx_60_day': 'TX 60-Day',
    'tx_90_day': 'TX 90-Day',
    'tx_bonded': 'TX Bonded',
    'tx_retainage': 'TX Retainage'
  };
  
  return legacyMap[noticeType] || noticeType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Get all unique notice type values for filtering (including legacy)
export const getAllNoticeTypeValues = () => {
  return NOTICE_TYPES.map(t => t.value);
};

// Advanced document types with state availability for cloning
export const DOCUMENT_TYPES = [
  { 
    value: 'mechanics_lien', 
    label: 'Mechanics Lien / Claim of Lien', 
    description: 'File a lien against property for unpaid work',
    states: 'all', // Available in all 50 states + DC
    requiresNotary: 'varies', // Varies by state - TX, CA, AZ, NV don't require
    category: 'lien',
    fee: 250,
    cloneableFrom: ['preliminary_notice_researched', 'preliminary_notice_not_researched', 'research_only']
  },
  { 
    value: 'satisfaction_of_lien', 
    label: 'Satisfaction / Release of Lien', 
    description: 'Release a previously filed lien after payment',
    states: 'all', // Available in all 50 states + DC
    requiresNotary: 'varies',
    category: 'lien',
    fee: 125,
    cloneableFrom: ['mechanics_lien'] // Clone from mechanics lien after payment received
  },
  { 
    value: 'notice_of_intent_to_lien', 
    label: 'Notice of Intent to Lien', 
    description: 'Pre-lien warning notice - required in some states before filing lien',
    states: ['AL', 'AR', 'CO', 'CT', 'IL', 'LA', 'MD', 'MO', 'NV', 'ND', 'PA', 'WI', 'WY'],
    requiresNotary: false,
    category: 'collection',
    fee: 75,
    cloneableFrom: ['preliminary_notice_researched', 'preliminary_notice_not_researched'],
    requiredBeforeLien: ['AR', 'CO', 'CT', 'LA', 'MO', 'ND', 'PA', 'WI', 'WY'] // Mandatory in these states
  },
  { 
    value: 'threat_to_lien', 
    label: 'Threat to Lien (Collection Letter)', 
    description: 'Collection tool - warning letter before filing lien',
    states: 'all',
    requiresNotary: false,
    category: 'collection',
    fee: 75,
    cloneableFrom: ['preliminary_notice_researched', 'preliminary_notice_not_researched']
  },
  { 
    value: 'claim_on_bond', 
    label: 'Claim on Payment Bond', 
    description: 'Claim against payment bond/surety for public projects',
    states: 'all', // Available in all states with Little Miller Acts
    requiresNotary: true,
    category: 'bond',
    fee: 150,
    cloneableFrom: ['preliminary_notice_researched', 'preliminary_notice_not_researched', 'research_only']
  },
  { 
    value: 'miller_act', 
    label: 'Miller Act Claim (Federal)', 
    description: 'Claim on federal project payment bond under Miller Act',
    states: 'all', // Federal projects in any state
    requiresNotary: true,
    category: 'bond',
    fee: 250,
    cloneableFrom: ['preliminary_notice_researched', 'preliminary_notice_not_researched']
  },
  { 
    value: 'notice_of_non_payment', 
    label: 'Notice of Non-Payment', 
    description: 'Pre-claim notice required before bond claims in some states',
    states: ['FL', 'SC', 'NC', 'GA'],
    requiresNotary: true,
    category: 'bond',
    fee: 75,
    cloneableFrom: ['preliminary_notice_researched', 'preliminary_notice_not_researched']
  },
  { 
    value: 'stop_payment_notice', 
    label: 'Stop Payment Notice', 
    description: 'Claim against construction loan funds held by lender',
    states: ['CA', 'AZ', 'NV', 'UT', 'WA'],
    requiresNotary: false,
    category: 'collection',
    fee: 100,
    cloneableFrom: ['preliminary_notice_researched', 'preliminary_notice_not_researched']
  },
  { 
    value: 'notice_of_completion', 
    label: 'Notice of Completion', 
    description: 'Records completion of construction - triggers lien filing deadlines',
    states: ['CA', 'NV', 'UT', 'AZ', 'WA', 'OR'],
    requiresNotary: true,
    category: 'completion',
    fee: 50,
    cloneableFrom: []
  },
  { 
    value: 'notice_of_cessation', 
    label: 'Notice of Cessation', 
    description: 'Records cessation/abandonment of work - triggers lien filing deadlines',
    states: ['CA', 'NV'],
    requiresNotary: true,
    category: 'completion',
    fee: 50,
    cloneableFrom: []
  }
];

// Get available document types for a specific state
export const getDocumentTypesForState = (state) => {
  if (!state) return [];
  return DOCUMENT_TYPES.filter(doc => 
    doc.states === 'all' || doc.states.includes(state.toUpperCase())
  );
};

// State-specific notarization requirements
// Some documents require notarization in some states but not others
// States where lien documents require notarization
// Based on STATE_LIEN_REQUIREMENTS from backend
const STATES_REQUIRING_LIEN_NOTARIZATION = [
  'AK', 'AL', 'AR', 'CO', 'CT', 'DC', 'DE', 'FL', 'GA', 'HI', 
  'IA', 'ID', 'IL', 'IN', 'KS', 'KY', 'LA', 'MA', 'MD', 'ME', 
  'MI', 'MN', 'MO', 'MS', 'MT', 'NC', 'ND', 'NE', 'NH', 'NJ', 
  'NM', 'NY', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 
  'UT', 'VA', 'VT', 'WA', 'WI', 'WV', 'WY'
];

// States NOT requiring notarization: AZ, CA, NV, TX

// Map of state -> document types that require notarization
const STATE_NOTARIZATION_MAP = {};

// Auto-generate map: all lien-related docs require notary in notarization states
STATES_REQUIRING_LIEN_NOTARIZATION.forEach(state => {
  STATE_NOTARIZATION_MAP[state] = ['lien', 'satisfaction_of_lien', 'notice_of_non_payment', 'claim_on_bond'];
});

// States that don't require notarization
['AZ', 'CA', 'NV', 'TX'].forEach(state => {
  STATE_NOTARIZATION_MAP[state] = [];
});

// Check if a specific document type requires notarization for a given state
export const documentRequiresNotary = (documentType, state) => {
  if (!state || !documentType) return false;
  const stateUpper = state.toUpperCase();
  const docTypeLower = documentType.toLowerCase();
  
  // Normalize document type (handle different naming conventions)
  const normalizedDocType = docTypeLower
    .replace('mechanics_lien', 'lien')
    .replace('mechanics-lien', 'lien');
  
  const stateDocs = STATE_NOTARIZATION_MAP[stateUpper];
  if (!stateDocs) return false;
  
  return stateDocs.includes(normalizedDocType);
};

// Get document display name for a state
export const getDocumentDisplayName = (documentType, state) => {
  const names = {
    lien: {
      FL: 'Claim of Lien',
      TX: 'Affidavit of Lien',
      CA: 'Mechanics Lien',
      GA: 'Claim of Lien',
      AZ: 'Notice & Claim of Lien',
      NV: 'Notice of Lien',
      SC: 'Notice and Certificate of Mechanics Lien',
      NC: 'Notice of Claim of Lien',
      CO: 'Statement of Lien',
      UT: 'Notice of Mechanics Lien'
    },
    satisfaction_of_lien: {
      FL: 'Satisfaction of Lien',
      TX: 'Release of Lien',
      CA: 'Release of Lien',
      GA: 'Satisfaction of Lien',
      AZ: 'Satisfaction of Lien',
      NV: 'Release of Lien',
      SC: 'Release of Mechanics Lien',
      NC: 'Release of Claim of Lien',
      CO: 'Release of Lien',
      UT: 'Release of Mechanics Lien'
    },
    claim_on_bond: 'Claim on Payment Bond',
    notice_of_non_payment: 'Notice of Non-Payment',
    notice_of_commencement: 'Notice of Commencement',
    stop_payment_notice: 'Stop Payment Notice',
    notice_of_intent: 'Notice of Intent to File Lien',
    threat_to_lien: 'Threat to Lien'
  };

  const docTypeLower = (documentType || '').toLowerCase()
    .replace('mechanics_lien', 'lien')
    .replace('mechanics-lien', 'lien');
  const stateUpper = (state || 'FL').toUpperCase();
  
  if (names[docTypeLower]) {
    if (typeof names[docTypeLower] === 'object') {
      return names[docTypeLower][stateUpper] || names[docTypeLower]['FL'] || docTypeLower.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    return names[docTypeLower];
  }
  return docTypeLower.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Status badge styling
export const getStatusBadgeClass = (status) => {
  const statusClasses = {
    'request': 'bg-blue-100 text-blue-800',
    'hold': 'bg-gray-100 text-gray-800',
    'research_in_progress': 'bg-yellow-100 text-yellow-800',
    'data_entry': 'bg-purple-100 text-purple-800',
    'pending_review': 'bg-orange-100 text-orange-800',
    'pending_payment': 'bg-orange-100 text-orange-800',
    'ready_to_send': 'bg-cyan-100 text-cyan-800',
    'sent': 'bg-green-100 text-green-800',
    'delivered': 'bg-emerald-100 text-emerald-800',
    'completed': 'bg-green-100 text-green-800',  // Legacy - maps to sent styling
    'recorded': 'bg-indigo-100 text-indigo-800',
    'revision': 'bg-amber-100 text-amber-800',
    'revision_requested': 'bg-pink-100 text-pink-800',
    'rescinded': 'bg-red-100 text-red-800',
    'rescind_requested': 'bg-red-100 text-red-800',
    'cancelled': 'bg-red-100 text-red-800',
    // Legacy status support
    'created': 'bg-blue-100 text-blue-800',
    'awaiting_research': 'bg-yellow-100 text-yellow-800',
    'research_complete': 'bg-purple-100 text-purple-800',
    'paid': 'bg-green-100 text-green-800'
  };
  return statusClasses[status] || 'bg-slate-100 text-slate-700';
};

// Order status definitions with workflow info
// Updated to match actual statuses used in the system
export const ORDER_STATUSES = [
  { value: 'created', label: 'Created', description: 'New order created', editable: true, color: 'bg-slate-100 text-slate-700' },
  { value: 'awaiting_research', label: 'Awaiting Research', description: 'Waiting for research to begin', editable: true, color: 'bg-blue-100 text-blue-700' },
  { value: 'research_complete', label: 'Research Complete', description: 'Research finished, ready for data entry', editable: true, color: 'bg-cyan-100 text-cyan-700' },
  { value: 'data_entry', label: 'Data Entry', description: 'Entering data from research', editable: true, color: 'bg-indigo-100 text-indigo-700' },
  { value: 'pending_review', label: 'Pending Review', description: 'Awaiting client review', editable: true, color: 'bg-purple-100 text-purple-700' },
  { value: 'pending_payment', label: 'Pending Payment', description: 'Awaiting payment', editable: true, color: 'bg-orange-100 text-orange-700' },
  { value: 'ready_to_send', label: 'Ready to Send', description: 'Paid, ready for mailing', editable: false, color: 'bg-amber-100 text-amber-700' },
  { value: 'sent', label: 'Sent', description: 'Notice has been mailed to recipients', editable: false, color: 'bg-green-100 text-green-700' },
  { value: 'delivered', label: 'Delivered', description: 'Delivery confirmed by USPS', editable: false, color: 'bg-emerald-100 text-emerald-700' },
  { value: 'hold', label: 'On Hold', description: 'Order paused', editable: true, color: 'bg-gray-100 text-gray-700' },
  { value: 'revision', label: 'Revision', description: 'Being revised for correction', editable: true, color: 'bg-yellow-100 text-yellow-700' },
  { value: 'revision_requested', label: 'Revision Requested', description: 'Client requested correction', editable: false, color: 'bg-yellow-100 text-yellow-700' },
  { value: 'rescind_requested', label: 'Rescind Requested', description: 'Client requested retraction', editable: false, color: 'bg-red-100 text-red-700' },
  { value: 'rescinded', label: 'Rescinded', description: 'Notice has been rescinded', editable: false, color: 'bg-red-100 text-red-700' },
  { value: 'cancelled', label: 'Cancelled', description: 'Order cancelled', editable: false, color: 'bg-slate-100 text-slate-500' },
];

// Format status for display
export const formatStatus = (status) => {
  // Normalize 'completed' to 'sent' for display
  if (status === 'completed') status = 'sent';
  return status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
};

// Helper: Check if order is in a "mailed/finished" state
// Use this instead of checking for both 'sent' and 'completed'
export const isMailedStatus = (status) => {
  return ['sent', 'completed', 'delivered'].includes(status);
};

// Helper: Check if order can have revision/rescind actions
export const canReviseOrRescind = (status) => {
  return ['sent', 'completed', 'delivered', 'revision_requested'].includes(status);
};

// US States
export const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];
